import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, Filler);

// Styled Components
const CardWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

// Clean PLN value (e.g., "1 234,56 PLN" → 1234.56)
const cleanPLN = (value) => {
  if (!value) return 0;
  return parseFloat(value.toString().replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const getQuarter = (month) => {
  if (month >= 1 && month <= 3) return "Q1";
  if (month >= 4 && month <= 6) return "Q2";
  if (month >= 7 && month <= 9) return "Q3";
  if (month >= 10 && month <= 12) return "Q4";
  return null;
};

const QuarterlyPurchaseComparison = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const groupedData = data.reduce((acc, row) => {
      let year, month;

      if (row.Rok && row.Miesiac) {
        year = parseInt(row.Rok);
        month = parseInt(row.Miesiac);
      } else if (row.DataZakupu) {
        const parsed = new Date(row.DataZakupu);
        if (!isNaN(parsed)) {
          year = parsed.getFullYear();
          month = parsed.getMonth() + 1;
        }
      }

      if (!year || !month) return acc;

      const quarter = getQuarter(month);
      const amount = cleanPLN(row.K_42); // 💡 Only K_42 is valid for zakupy

      if (!acc[year]) acc[year] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
      if (quarter) acc[year][quarter] += amount;

      return acc;
    }, {});

    const years = Object.keys(groupedData).map(Number);
    const latestYear = Math.max(...years);

    setSalesData(groupedData);
    setAvailableYears(years.sort((a, b) => b - a));
    setSelectedYear(latestYear);
  }, [data]);

  useEffect(() => {
    if (!selectedYear || !salesData[selectedYear]) return;

    const quarters = ["Q1", "Q2", "Q3", "Q4"];
    const currentYearData = quarters.map((q) => salesData[selectedYear]?.[q] || 0);
    const previousYearData = quarters.map((q) => salesData[selectedYear - 1]?.[q] || 0);

    setChartData({
      labels: quarters,
      datasets: [
        {
          label: `Zakupy ${selectedYear} (PLN)`,
          data: currentYearData,
          borderColor: "#9848c7",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: true,
          tension: 0.4,
          pointBackgroundColor: "#9848c7",
        },
        {
          label: `Zakupy ${selectedYear - 1} (PLN)`,
          data: previousYearData,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
          tension: 0.4,
          pointBackgroundColor: "rgba(255, 99, 132, 1)",
        },
      ],
    });
  }, [selectedYear, salesData]);

  if (!chartData) return <p>Ładowanie danych zakupów kwartalnych...</p>;

  return (
    <CardWrapper>
      <ChartWrapper>
        <InfoIconWrapper data-tooltip-id="quarterly-zakupy-tooltip">
          <Info />
        </InfoIconWrapper>

        <InfoTooltip
          id="quarterly-zakupy-tooltip"
          place="left"
          content={
            <>
              <strong>Porównanie kwartalne zakupów</strong>
              <br />
              Zakupy w podziale na kwartały dla {selectedYear} vs {selectedYear - 1}
            </>
          }
        />

        <FiltersWrapper>
          <div>
            <label htmlFor="year-select">Rok:</label>
            <select
              id="year-select"
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </FiltersWrapper>

        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Zakupy (PLN)",
                },
                ticks: {
                  callback: (value) =>
                    Number(value)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, " "),
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Zakupy kwartalne: ${selectedYear} vs ${selectedYear - 1}`,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || "";
                    const value = context.parsed.y || 0;
                    return `${label}: ${value.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                    })}`;
                  },
                },
              },
            },
          }}
        />
      </ChartWrapper>
    </CardWrapper>
  );
};

export default QuarterlyPurchaseComparison;
